import { warn } from '@components/utils';

import type { NTree } from '../../CdsTree/interface';

interface IConductReturnType {
  checkedKeys: NTree.TKey[];
  halfCheckedKeys: NTree.TKey[];
}

export function isCheckDisabled<TreeDataType>(node: TreeDataType) {
  const { disabled, disableCheckbox, checkable } = (node || {}) as NTree.IDataNode;
  return !!(disabled || disableCheckbox) || checkable === false;
}

export function conductCheck<TreeDataType extends NTree.IBasicDataNode = NTree.IDataNode>(
  keyList: NTree.TKey[],
  checked: true | { checked: false; halfCheckedKeys: NTree.TKey[] },
  keyEntities: Record<NTree.TKey, NTree.IDataEntity<TreeDataType>>,
  maxLevel: number,
  levelEntities: Map<number, Set<NTree.IDataEntity<TreeDataType>>>,
  getCheckedDisabled?: NTree.TGetCheckDisabled<TreeDataType>,
  preserveSelectedRowKeys?: boolean
): IConductReturnType {
  const warningMissKey: NTree.TKey[] = [];

  let syntheticGetCheckDisabled: NTree.TGetCheckDisabled<TreeDataType>;
  if (getCheckedDisabled) {
    syntheticGetCheckDisabled = getCheckedDisabled;
  } else {
    syntheticGetCheckDisabled = isCheckDisabled;
  }

  // Handle exist keys or all keys if `preserveSelectedRowKeys` is true
  const keys = new Set<NTree.TKey>(
    keyList.filter((key) => {
      const hasEntity = !!keyEntities[key];

      if (!hasEntity) {
        warningMissKey.push(key);
      }

      return !!preserveSelectedRowKeys || hasEntity;
    })
  );

  if (warningMissKey.length) {
    warn(
      'CdsTree',
      `Tree missing follow keys: ${warningMissKey
        .slice(0, 100)
        .map((key) => `'${key}'`)
        .join(', ')}`
    );
  }

  let result: IConductReturnType;

  if (checked === true) {
    result = fillConductCheck<TreeDataType>(keys, levelEntities, maxLevel, syntheticGetCheckDisabled);
  } else {
    result = cleanConductCheck<TreeDataType>(
      keys,
      checked.halfCheckedKeys,
      levelEntities,
      maxLevel,
      syntheticGetCheckDisabled
    );
  }

  return result;
}

function fillConductCheck<TreeDataType extends NTree.IBasicDataNode = NTree.IDataNode>(
  keys: Set<NTree.TKey>,
  levelEntities: Map<number, Set<NTree.IDataEntity<TreeDataType>>>,
  maxLevel: number,
  syntheticGetCheckDisabled: NTree.TGetCheckDisabled<TreeDataType>
): IConductReturnType {
  const checkedKeys = new Set<NTree.TKey>(keys);
  const halfCheckedKeys = new Set<NTree.TKey>();

  // Add checked keys top to bottom
  for (let level = 0; level <= maxLevel; level += 1) {
    const entities = levelEntities.get(level) || new Set();

    entities.forEach((entity) => {
      const { key, node, children = [] } = entity;

      if (checkedKeys.has(key) && !syntheticGetCheckDisabled(node)) {
        children
          .filter((childEntity) => !syntheticGetCheckDisabled(childEntity.node))
          .forEach((childEntity) => {
            checkedKeys.add(childEntity.key);
          });
      }
    });
  }

  // Add checked keys from bottom to top
  const visitedKeys = new Set<NTree.TKey>();
  for (let level = maxLevel; level >= 0; level -= 1) {
    const entities = levelEntities.get(level) || new Set();

    entities.forEach((entity) => {
      // Skip if no need to check
      if (syntheticGetCheckDisabled(entity.node) || !entity.parent || visitedKeys.has(entity.parent.key)) return;

      // Skip if parent is disabled
      if (syntheticGetCheckDisabled(entity.parent.node)) {
        visitedKeys.add(entity.parent.key);
        return;
      }

      let allChecked = true;
      let partialChecked = false;

      (entity.parent.children || [])
        .filter((childEntity) => !syntheticGetCheckDisabled(childEntity.node))
        .forEach(({ key }) => {
          const checked = checkedKeys.has(key);

          if (allChecked && !checked) {
            allChecked = false;
          }

          if (!partialChecked && (checked || halfCheckedKeys.has(key))) {
            partialChecked = true;
          }
        });

      if (allChecked) {
        checkedKeys.add(entity.parent.key);
      }

      if (partialChecked) {
        halfCheckedKeys.add(entity.parent.key);
      }

      visitedKeys.add(entity.parent.key);
    });
  }

  return {
    checkedKeys: Array.from(checkedKeys) || [],
    halfCheckedKeys: Array.from(removeFromCheckedKeys(halfCheckedKeys, checkedKeys)) || []
  };
}

function cleanConductCheck<TreeDataType extends NTree.IBasicDataNode = NTree.IDataNode>(
  keys: Set<NTree.TKey>,
  halfKeys: NTree.TKey[],
  levelEntities: Map<number, Set<NTree.IDataEntity<TreeDataType>>>,
  maxLevel: number,
  syntheticGetCheckDisabled: NTree.TGetCheckDisabled<TreeDataType>
): IConductReturnType {
  const checkedKeys = new Set<NTree.TKey>(keys);
  let halfCheckedKeys = new Set<NTree.TKey>(halfKeys);

  for (let level = 0; level <= maxLevel; level += 1) {
    const entities = levelEntities.get(level) || new Set();

    entities.forEach((entity) => {
      const { key, node, children = [] } = entity;

      if (!checkedKeys.has(key) && !halfCheckedKeys.has(key) && !syntheticGetCheckDisabled(node)) {
        children
          .filter((childEntity) => !syntheticGetCheckDisabled(childEntity.node))
          .forEach((childEntity) => {
            checkedKeys.delete(childEntity.key);
          });
      }
    });
  }

  halfCheckedKeys = new Set<NTree.TKey>();
  const visitedKeys = new Set<NTree.TKey>();

  for (let level = maxLevel; level >= 0; level -= 1) {
    const entities = levelEntities.get(level) || new Set();

    entities.forEach((entity) => {
      if (syntheticGetCheckDisabled(entity.node) || !entity.parent || visitedKeys.has(entity.parent.key)) return;

      if (syntheticGetCheckDisabled(entity.parent.node)) {
        visitedKeys.add(entity.parent.key);
        return;
      }

      let allChecked = true;
      let partialChecked = false;

      (entity.parent.children || [])
        .filter((childEntity) => !syntheticGetCheckDisabled(childEntity.node))
        .forEach(({ key }) => {
          const checked = checkedKeys.has(key);

          if (allChecked && !checked) {
            allChecked = false;
          }

          if (!partialChecked && (checked || halfCheckedKeys.has(key))) {
            partialChecked = true;
          }
        });

      if (!allChecked) {
        checkedKeys.delete(entity.parent.key);
      }

      if (partialChecked) {
        halfCheckedKeys.add(entity.parent.key);
      }

      visitedKeys.add(entity.parent.key);
    });
  }

  return {
    checkedKeys: Array.from(checkedKeys) || [],
    halfCheckedKeys: Array.from(removeFromCheckedKeys(halfCheckedKeys, checkedKeys)) || []
  };
}

function removeFromCheckedKeys(halfCheckedKeys: Set<NTree.TKey>, checkedKeys: Set<NTree.TKey>) {
  const filteredKeys = new Set<NTree.TKey>();

  halfCheckedKeys.forEach((key) => {
    if (!checkedKeys.has(key)) {
      filteredKeys.add(key);
    }
  });

  return filteredKeys;
}
